import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCw, RotateCcw } from "lucide-react";
import { captureError } from "../../services/sentryInit";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showStack: boolean;
  showComponentStack: boolean;
  /** Incremented on reset to force a child re-mount via key. */
  resetCount: number;
  /** Captured once at error-time so the error code/timestamp is stable. */
  errorId: string | null;
  /** Prevents infinite retry loops when the child always throws. */
  retries: number;
  /** Timestamp of the last attempted retry — used to re-arm the retry budget. */
  lastRetryAt: number | null;
}

// Auth / session state we intentionally preserve when the user triggers
// "clear cache + reload" — signing them out of every workspace on a render
// crash would be hostile, not helpful. Mirrors the keys used by apiClient.ts
// and the org/workspace context providers.
const PRESERVED_STORAGE_KEYS = [
  "adjudon_token",
  "adjudon_refresh_token",
  "adjudon_workspace_id",
  "adjudon_org_id",
  // Mirrors WorkspaceDashboardPage.ACTIVE_WS_KEY / Sidebar.tsx — losing this
  // drops the user back to a default workspace after a render crash, which
  // feels like a silent logout for multi-workspace accounts.
  "adjudon_active_workspace",
];

const MAX_RETRIES = 3;
// Minutes after which a successful recovery re-arms the retry budget. If the
// user's first retry actually worked and then a new crash happens an hour
// later, that's a different incident — don't tell them "Retry limit reached".
const RETRY_WINDOW_MS = 5 * 60 * 1000;

// Crockford-inspired alphabet: excludes I, L, O, U, and all digits that
// collide with them (0/1). Support-ticket readers routinely confuse "I/1/l"
// and "O/0" in handwritten or low-resolution screenshots, which misroutes
// the ticket. Trading a bit of entropy for unambiguous readability.
const ERROR_ID_ALPHABET = "23456789ABCDEFGHJKMNPQRSTVWXYZ";

function randomErrorSuffix(length: number): string {
  let out = "";
  for (let i = 0; i < length; i++) {
    out += ERROR_ID_ALPHABET[Math.floor(Math.random() * ERROR_ID_ALPHABET.length)];
  }
  return out;
}

function makeErrorId(): string {
  // Short human-readable reference; matches what the user sees in the
  // support CTA so they can paste it into an email. 8 random chars keeps
  // collision probability negligible across a busy support inbox.
  const ts = Date.now().toString(36).toUpperCase();
  return `ERR-${ts}-${randomErrorSuffix(8)}`;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showStack: false,
      showComponentStack: false,
      resetCount: 0,
      errorId: null,
      retries: 0,
      lastRetryAt: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // `retries`/`lastRetryAt` deliberately NOT reset here: a fresh error
    // mid-retry-window must still count against the existing budget so a
    // child that throws on every mount can't spin forever. The
    // RETRY_WINDOW_MS in handleReset handles re-arming for genuinely new
    // incidents that happen after the window has lapsed.
    return { hasError: true, error, errorId: makeErrorId() };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Capture to Sentry before anything else — setState below may trigger
    // a fallback render that itself throws, and we'd lose the original.
    captureError(error, { componentStack: errorInfo.componentStack ?? undefined });

    // Raw error.message can leak internal URLs, IDs, or stack fragments —
    // CLAUDE.md §4 forbids that in production logs. DEV keeps full detail
    // for engineers; prod surfaces only the stable errorId.
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error("ErrorBoundary caught:", error, errorInfo);
    }

    this.setState({ errorInfo });
  }

  handleReset = () => {
    this.setState((prev) => {
      const now = Date.now();
      // Re-arm the retry budget if the last retry attempt was long enough ago
      // that this is effectively a new incident — not a retry storm on the
      // same underlying failure.
      const withinWindow =
        prev.lastRetryAt !== null && now - prev.lastRetryAt < RETRY_WINDOW_MS;
      const effectiveRetries = withinWindow ? prev.retries : 0;

      // Hard stop after MAX_RETRIES within the retry window to avoid an
      // infinite retry loop when the child consistently throws on mount.
      if (effectiveRetries >= MAX_RETRIES) return prev;

      return {
        hasError: false,
        error: null,
        errorInfo: null,
        showStack: false,
        showComponentStack: false,
        resetCount: prev.resetCount + 1,
        errorId: null,
        retries: effectiveRetries + 1,
        lastRetryAt: now,
      };
    });
  };

  handleClearCacheAndReload = async () => {
    try {
      // Preserve auth so a crash doesn't silently log the user out.
      const preserved: Record<string, string> = {};
      for (const key of PRESERVED_STORAGE_KEYS) {
        const value = localStorage.getItem(key);
        if (value !== null) preserved[key] = value;
      }

      if ("caches" in window) {
        const cacheNames = await caches.keys();
        // `allSettled` so one cache-delete failure doesn't strand us before
        // localStorage is cleared below — a partial cleanup is still better
        // than none.
        await Promise.allSettled(cacheNames.map((name) => caches.delete(name)));
      }

      localStorage.clear();
      sessionStorage.clear();

      for (const [key, value] of Object.entries(preserved)) {
        try {
          localStorage.setItem(key, value);
        } catch (err) {
          // QuotaExceededError or disabled storage — we've already logged the
          // user out by clearing above. Warn in DEV so we notice rather than
          // silently dropping the auth token on reload.
          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.warn(`[ErrorBoundary] failed to restore "${key}":`, err);
          }
        }
      }

      window.location.reload();
    } catch (error) {
      // Last-ditch reload: never let a cleanup failure trap the user on
      // the broken screen. Stay silent in prod (CLAUDE.md §4).
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.error("Failed to clear cache:", error);
      }
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Stable values captured at error-time. Using new Date() inline would
      // drift on every re-render and make support tickets unreproducible.
      const errorId = this.state.errorId ?? "ERR-UNKNOWN";
      const canRetry = this.state.retries < MAX_RETRIES;
      // Surface raw error.message only in DEV — in prod we show a generic
      // line so internal paths / IDs / PII never reach the screen.
      const devMessage = import.meta.env.DEV ? this.state.error?.message : null;

      return (
        <div
          role="alert"
          aria-live="assertive"
          aria-label="Application error"
          className="min-h-screen bg-background flex items-center justify-center p-4 sm:p-6"
        >
          <div className="max-w-2xl w-full text-center">
            <div className="mb-6 sm:mb-8 flex justify-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[var(--surface-2)] border border-border flex items-center justify-center">
                <AlertTriangle
                  className="w-6 h-6 sm:w-8 sm:h-8 text-review"
                  aria-hidden="true"
                />
              </div>
            </div>

            <h1 className="text-xl sm:text-2xl font-bold text-foreground mb-2 sm:mb-3">
              Application error
            </h1>

            <p className="text-sm sm:text-base text-muted-foreground mb-6 sm:mb-8">
              {devMessage ?? "Something went wrong while rendering this page."}
            </p>

            {import.meta.env.DEV && this.state.error?.stack && (
              <div className="mb-6 sm:mb-8">
                <div className="text-left">
                  <button
                    type="button"
                    onClick={() => this.setState((s) => ({ showStack: !s.showStack }))}
                    className="text-xs sm:text-sm text-foreground hover:text-muted-foreground mb-2"
                  >
                    Show error stack trace {this.state.showStack ? "▴" : "▾"}
                  </button>
                  {this.state.showStack && (
                    <pre className="text-[10px] sm:text-xs bg-[var(--surface-1)] border border-border p-3 sm:p-4 overflow-auto max-h-40 sm:max-h-60">
                      {this.state.error.stack}
                    </pre>
                  )}
                </div>

                {this.state.errorInfo?.componentStack && (
                  <div className="text-left mt-3">
                    <button
                      type="button"
                      onClick={() =>
                        this.setState((s) => ({ showComponentStack: !s.showComponentStack }))
                      }
                      className="text-xs sm:text-sm text-foreground hover:text-muted-foreground mb-2"
                    >
                      Show component stack {this.state.showComponentStack ? "▴" : "▾"}
                    </button>
                    {this.state.showComponentStack && (
                      <pre className="text-[10px] sm:text-xs bg-[var(--surface-1)] border border-border p-3 sm:p-4 overflow-auto max-h-40 sm:max-h-60">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-6 sm:mb-8">
              <button
                type="button"
                onClick={this.handleClearCacheAndReload}
                className="px-4 sm:px-6 py-3 adp-btn-primary text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
              >
                <RefreshCw className="w-4 h-4" aria-hidden="true" />
                <span>Reload application</span>
              </button>

              <button
                type="button"
                onClick={this.handleReset}
                disabled={!canRetry}
                className="px-4 sm:px-6 py-3 bg-[var(--surface-1)] hover:bg-[var(--surface-2)] border border-border text-foreground text-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RotateCcw className="w-4 h-4" aria-hidden="true" />
                <span>{canRetry ? "Try again" : "Retry limit reached"}</span>
              </button>
            </div>

            <div className="border-t border-border pt-6 sm:pt-8">
              <p className="text-xs text-muted-foreground mb-3 sm:mb-4">
                This error has been logged. If it persists, include the reference
                below when you contact support.
              </p>
              <div className="flex flex-wrap justify-center gap-2 sm:gap-3 text-xs sm:text-sm">
                <a
                  href={`mailto:support@adjudon.com?subject=Application+error+${errorId}`}
                  className="text-foreground hover:text-muted-foreground underline underline-offset-2 transition-colors"
                >
                  support@adjudon.com
                </a>
              </div>
            </div>

            <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-border">
              <p className="text-[10px] sm:text-xs text-mono text-muted-foreground">
                {errorId}
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Keying children on resetCount forces React to unmount + remount on
    // "Try again" — otherwise a stuck prop would just throw immediately.
    return <div key={this.state.resetCount}>{this.props.children}</div>;
  }
}
